import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Apple, Play } from "lucide-react";
import { DOWNLOAD_URL, APP_MOCKUP, ABSTRACT_ORB } from "../../lib/data";
import { useLanguage } from "../../lib/i18n";

const EASE = [0.16, 1, 0.3, 1];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({ y: "0%", transition: { duration: 1, ease: EASE, delay: 0.35 + i * 0.12 } }),
};

export const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const orbY = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 120]);

  const headline = t("hero.headline");

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden grid-lines" data-testid="hero-section">
      <motion.img src={ABSTRACT_ORB} alt="" style={{ y: orbY }} className="pointer-events-none absolute -top-20 -right-40 w-[70vw] max-w-[900px] opacity-60 mix-blend-screen select-none" />
      <div className="pointer-events-none absolute top-1/3 left-0 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[140px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full grid lg:grid-cols-12 gap-12 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }} className="inline-flex items-center gap-2 mb-8 px-4 py-1.5 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur">
            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
            <span className="text-xs uppercase tracking-[0.2em] text-slate-400">{t("hero.badge")}</span>
          </motion.div>

          <h1 className="font-head font-semibold text-5xl md:text-7xl lg:text-[5.2rem] tracking-tighter leading-[1.02]">
            {headline.map((line, i) => (
              <span key={i} className="line-mask">
                <motion.span className="block text-gradient" custom={i} variants={lineVariant} initial="hidden" animate="visible">
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.9 }} className="mt-8 max-w-xl text-lg md:text-xl text-slate-400 leading-relaxed">
            {t("hero.subtitle")}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 1.05 }} className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <a href={DOWNLOAD_URL} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2.5 px-7 py-4 rounded-full bg-blue-500 text-white font-semibold hover:bg-blue-600 transition-colors duration-300 glow-blue hover:scale-[1.02] will-change-transform" data-testid="hero-download-btn">
              {t("hero.downloadNow")}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </a>

            <div className="inline-flex items-center gap-3 px-6 py-4 rounded-full border border-white/10 bg-white/[0.02] opacity-50 cursor-not-allowed select-none" data-testid="hero-comingsoon-btn" aria-disabled="true">
              <span className="flex gap-1.5 text-slate-400">
                <Apple size={18} />
                <Play size={18} />
              </span>
              <span className="text-sm text-slate-400">{t("hero.comingSoon")}</span>
            </div>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: phoneY }} className="lg:col-span-5 relative flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 -m-16 bg-blue-500/20 rounded-full blur-[100px]" />
            <motion.img src={APP_MOCKUP} alt="Rexx Calling app interface" initial={{ opacity: 0, scale: 0.9, y: 40 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 1.2, ease: EASE, delay: 0.6 }} className="relative z-10 w-[300px] md:w-[360px] drop-shadow-[0_40px_80px_rgba(0,0,0,0.6)]" data-testid="hero-app-mockup" />
          </div>
        </motion.div>
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.3em] text-slate-600">{t("hero.scroll")}</span>
        <div className="w-px h-10 bg-gradient-to-b from-slate-500 to-transparent" />
      </motion.div>
    </section>
  );
};
